import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HandCoins, Percent, Info } from "lucide-react";

const SECTION_ICONS = [
  <HandCoins className="w-7 h-7" />,
  <Percent className="w-7 h-7" />,
];

const AboutUs = () => {
  const { t } = useTranslation();
  const sections = t("aboutUs.sections", { returnObjects: true }).map((s, i) => ({
    ...s,
    icon: SECTION_ICONS[i],
  }));
  const feeRows = t("aboutUs.feeRows", { returnObjects: true });

  return (
    <div className="min-h-screen bg-light-grey pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* ── Header ─────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-primary/10 text-primary font-bold text-sm tracking-wide px-4 py-1.5 rounded-full mb-4">
            {t("aboutUs.eyebrow")}
          </span>
          <h1 className="text-3xl md:text-4xl font-black text-dark-grey mb-4">
            {t("aboutUs.title")}
          </h1>
          <p className="text-grey text-lg max-w-2xl mx-auto leading-relaxed">
            {t("aboutUs.subtitle")}
          </p>
        </motion.div>

        {/* ── Section Cards ──────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-card p-6 border border-transparent hover:border-primary/20 transition-colors duration-300"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-medium-blue rounded-xl flex items-center justify-center mb-4 text-white shadow-lg shadow-primary/30">
                {section.icon}
              </div>
              <h3 className="font-bold text-dark-grey text-lg mb-2">{section.title}</h3>
              <p className="text-grey text-sm leading-relaxed">{section.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* ── Fee Table ──────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-xl shadow-card overflow-hidden mb-10"
        >
          <h3 className="font-bold text-dark-grey text-lg text-center pt-6 px-6">
            {t("aboutUs.feeTableTitle")}
          </h3>
          <div className="overflow-x-auto p-6">
            <table className="w-full [text-align:start]">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 rounded-s-xl font-semibold">{t("aboutUs.tableItemValue")}</th>
                  <th className="py-3 px-4 font-semibold">{t("aboutUs.tableFee")}</th>
                  <th className="py-3 px-4 rounded-e-xl font-semibold">{t("aboutUs.tableCondition")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey/20">
                {feeRows.map((row, index) => (
                  <tr key={index} className="hover:bg-light-grey transition-colors duration-200">
                    <td className="py-3 px-4 text-dark-grey">{row.itemValue}</td>
                    <td className="py-3 px-4 font-bold text-primary">{row.fee}</td>
                    <td className="py-3 px-4 text-grey text-sm">
                      {row.condition
                        ? row.condition.split("\n").map((line, idx) => (
                            <span key={idx}>
                              {line}
                              <br />
                            </span>
                          ))
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* ── Closing Note ───────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-primary/5 border border-primary/20 rounded-xl p-6 flex gap-4 items-start"
        >
          <Info className="w-6 h-6 text-primary shrink-0 mt-1" />
          <p className="text-grey leading-relaxed text-sm">
            {t("aboutUs.closingNote")}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUs;
